import { StyledButton, StyledButtonGroup } from "@components/button";
import { StyledFieldsWrapper } from "@components/inputs/field/FieldsWrapper.styles";
import { StyledSkeleton } from "@components/skeleton/Skeleton.styles";
import { transparentize } from "polished";
import styled, { css } from "styled-components";

import Alert from "../../components/alert";
import SmartHeader from "../../components/smart/smartHeader";
import Tabs from "../../components/tabs/Tabs";
import { LocalStackOrder, T_PLAIN_big, T_PLAIN_small } from "../../global.style";
import { TableWrapper } from "../table/TableView.styles";
import View from "../View";
import { VIEW_PADDING_VALUE } from "../View.styles";

export const FormFooter = styled.div<{
    hasTopMargin?: boolean;
    isEditOverride?: boolean;
    $addViewHorizontalPadding?: boolean;
}>`
    display: flex;
    /* Top margin reserves space for possible alert, but do we need this always (e.g. in read-only state)? */
    margin-top: ${props => props.hasTopMargin === false ? 0 : "22px"};
    justify-content: flex-end;

    ${StyledButton}, ${StyledSkeleton} {
        margin-bottom: ${VIEW_PADDING_VALUE}px;
    }

    ${StyledButtonGroup} {
        justify-content: flex-end;
    }

    ${props => props.isEditOverride && css`
        z-index: ${LocalStackOrder.AboveContent + 1};
    `}

    ${props => props.$addViewHorizontalPadding && css`
        padding-left: ${VIEW_PADDING_VALUE}px;
        padding-right: ${VIEW_PADDING_VALUE}px;
    `}
`;

interface IFormContentProps {
    autocomplete?: string;
    buttonsOnBottom?: boolean;
}

export const FormContent = styled.form.attrs<IFormContentProps>({
    autocomplete: "off"
})<{
    withoutPadding?: boolean;
    isEditOverride?: boolean;
}>`
    position: relative;
    ${props => !props.withoutPadding && css`
        padding-bottom: ${VIEW_PADDING_VALUE}px;
    `};
    ${props => props.buttonsOnBottom && css`
        flex: 1 1 auto;
    `};
    ${props => props.isEditOverride && css`
        fieldset, ${LockedFormEditOverrideStatus} {
            z-index: ${LocalStackOrder.AboveContent + 1};
        }
    `};
`;

export const FormHeaderTopMargin = 12;

export const SmartHeaderStyled = styled(SmartHeader) <{
    hideBreadcrumbs?: boolean
}>`
`;

export const FormAlertContainer = styled(Alert)`
    margin-bottom: 30px;
`;

export const StyledErrorWrapper = styled.div`
    margin-bottom: 19px;
`;

export const LockedFormEditOverrideStatus = styled.div`
    position: relative;
    display: flex;
    gap: 6px;
    align-items: center;
    margin-bottom: 42px;
    padding: 4px 0 4px 18px;
    color: ${props => props.theme.C_ACT_main};
    background-color: ${props => props.theme.C_BG_tooltip};
    border-radius: ${props => props.theme.borderRadius};
    ${T_PLAIN_small};
`;

export const LockedFormOverrideOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${props => transparentize(0.7, props.theme.C_BG_tooltip)};
    z-index: ${LocalStackOrder.AboveContent};
    // idk how to do this properly without pointer-events: none
    // hopefully UX won't care about the elements under the overlay being clickable
    pointer-events: none;
`;

export const FormTabs = styled(Tabs) <{
    hasTitle?: boolean;
    isFirst?: boolean;
}>`
    margin-top: ${props => props.isFirst ? 0 : props.hasTitle ? "35px" : "65px"};

    ${TableWrapper} {
        margin-top: 0;
    }
`;

export const ViewStyled = styled(View)<{
    $useHorizontalScrollbar?: boolean;
}>`
    
    // instead of wrapping form inputs, use horizontal scrollbar to show all items
    ${props => props.$useHorizontalScrollbar && css`
        overflow: auto;
        width: fit-content;
        
        ${StyledFieldsWrapper} {
            flex-wrap: nowrap;
        }
    `}
`;

export const FormDescriptionText = styled.div`
    ${T_PLAIN_big};
    color: ${props => props.theme.C_TEXT_primary};
`;